import {
  useRef,
  useEffect,
  memo,
  CSSProperties,
  useMemo,
  useState,
} from 'react';
import JsBarcode from 'jsbarcode';
import { PriceTagInfo } from 'types/pms';
import { ReplaceAll } from 'utils/handleObject';
import { useDrag } from 'react-dnd';
import dayjs from 'dayjs';
import classNames from 'classnames';
import useBrandList from 'commons/hooks/use-brand-list';
import { findLabelByValue } from 'commons/options';
import i18n from 'i18n';
import { Form, Input, Modal, QRCode } from 'antd';
import { EditOutlined } from '@ant-design/icons';
import styles from './index.module.scss';
import { priceToWithoutTax } from 'utils/price-change';
import { CURRENCY_MAP } from 'constant/currency';
import { LANGUAGE_MAP } from 'constant/language';

const LangageAttribute = {
  color: {
    ja: '色',
    en: 'Color',
    fr: 'Couleur',
  },
  material: {
    ja: '素材',
    en: 'Material',
    fr: 'Matériel',
  },
  hardware: {
    ja: '金具',
    en: 'Hardware',
    fr: 'Quincaillerie',
  },
  stamp: {
    ja: '刻印',
    en: 'Stamp',
    fr: 'Tampon',
  },
};

interface Props {
  data: PriceTagInfo;
  size?: 'default' | 'large';
  type?: string;
  isDropped?: boolean;
  isRef?: boolean;
  style?: CSSProperties;
  setDragging?: (data: boolean) => void;
  parentDragging: boolean;
  passportUrl?: string;
  showFullStamp?: boolean;
}

const ItemClass =
  'w-full text-[8px] leading-[10.6667px] font-light tracking-normal inline-flex flex-row overflow-hidden box-border';
const ItemLabelClass = 'font-HiraginoMinchoPron box-border text-black';
const ItemLabelSpanClass =
  'text-black font-HiraginoMinchoPron text-justify box-border';

const PriceTag = ({
  data,
  size = 'default',
  type,
  isRef,
  setDragging,
  passportUrl,
  showFullStamp = false,
}: Props) => {
  const barCodeRef = useRef(null);

  const lang = useMemo(() => {
    if (data.currency === CURRENCY_MAP.JPY) {
      return LANGUAGE_MAP.JA;
    }

    if (data.currency === CURRENCY_MAP.EUR) {
      return LANGUAGE_MAP.FR;
    }

    return LANGUAGE_MAP.EN;
  }, [data.currency]);

  const { BrandList, DefaultBrand } = useBrandList(
    lang === LANGUAGE_MAP.FR ? LANGUAGE_MAP.EN : lang,
  );
  const [dataSource, setDataSource] = useState<PriceTagInfo>(data);
  const [form] = Form.useForm();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    setDataSource(data);
  }, [data]);

  useEffect(() => {
    if (dataSource && barCodeRef.current) {
      const options = {
        height: 50,
        displayValue: false,
      };
      JsBarcode(barCodeRef.current, dataSource.id.toString(), options);
    }
  }, [dataSource, size]);

  const [{ isDragging }, drag]: any = useDrag(
    () =>
      ({
        type,
        item: { ...data },
        collect: (monitor: { isDragging: () => any }) => ({
          opacity: monitor.isDragging() ? 0.4 : 1,
          isDragging: monitor.isDragging(),
        }),
        end: (item: { name: any }, monitor: { getDropResult: () => any }) => {
          const dropResult = monitor.getDropResult();
          if (item && dropResult) {
            console.log(`You dropped ${item.name}`);
          }
        },
      }) as any,
    [type],
  );

  /** 拖拽中，抛出上层做处理 */
  useEffect(() => {
    if (setDragging) setDragging(isDragging);
  }, [isDragging, setDragging]);

  const getDatePrice = useMemo(() => {
    let out = '';
    let YY = '';
    // 优先使用入库时间，没有入库时间则使用创建时间
    const timeToUse = dataSource.entryTime || dataSource.createTime;
    if (timeToUse) {
      const time = dayjs(timeToUse);
      const DD = time.format('DD');
      YY = time.format('YY');
      out = `${time.format('MM')}${DD[0]}-${DD[1]}`;
    }

    if (dataSource.costPrice) {
      // 间隔加密：只对奇数位置(1,3,5...)的数字进行加密，偶数位置保持不变
      // 数字转字母加密：1→A, 2→B, 3→C, ..., 9→I, 0→保持不变
      const encryptedPrice = dataSource.costPrice
        .toString()
        .split('')
        .map((digit, index) => {
          // 奇数位置（index为偶数，因为从0开始）进行加密
          if (index % 2 === 0 && digit >= '1' && digit <= '9') {
            return String.fromCharCode(64 + parseInt(digit)); // A=65, 所以64+1=65(A)
          }
          return digit; // 偶数位置或0保持不变
        })
        .join('');
      out = `${out}${encryptedPrice}${YY}`;
    }

    return out;
  }, [dataSource.entryTime, dataSource.createTime, dataSource.costPrice]);

  return (
    <div
      ref={isRef ? drag : null}
      data-testid="box"
      className="text-black grid gap-[6px] text-xs leading-none"
    >
      {dataSource && dataSource.productSn ? (
        <>
          {passportUrl && (
            <div className="flex justify-center">
              <QRCode
                size={60}
                bordered={false}
                value={passportUrl}
                type="svg"
                style={{ padding: 0, borderRadius: 0 }}
              />
            </div>
          )}
          <div>
            <div
              className={classNames(
                'font-Georgia font-bold tracking-normal text-center uppercase',
                passportUrl ? 'text-xs' : 'text-sm',
              )}
            >
              {findLabelByValue(dataSource.brandName, BrandList, DefaultBrand)}
            </div>

            {/* 标题 */}
            <div
              className={classNames(
                'font-Georgia font-bold break-words text-center leading-none mx-[8px]',
                passportUrl && 'text-[10px]',
              )}
            >
              {dataSource.name || '-'}
            </div>
          </div>

          {/* 信息展示 */}
          <div className="mx-[10.6667px] box-border flex justify-center">
            <div className="inline-flex flex-col box-border gap-1">
              {/* rank */}
              <div className="font-HiraginoMinchoPron text-[8px] font-normal tracking-normal text-center">
                {dataSource.rank || '-'}
              </div>
              {/* 材质 */}
              {dataSource.material && (
                <div className={ItemClass}>
                  <div
                    className={ItemLabelClass}
                    style={{ display: 'flex', alignItems: 'flex-start' }}
                  >
                    <span style={{ whiteSpace: 'nowrap' }}>
                      {LangageAttribute.material[lang]}:
                    </span>
                    <div style={{ marginLeft: '2px' }}>
                      {(dataSource.material || '-')
                        .split(',')
                        .map((item, index) => (
                          <div key={index} style={{ lineHeight: '1.3' }}>
                            {ReplaceAll(item as string) || '-'}
                          </div>
                        ))}
                    </div>
                  </div>
                </div>
              )}
              {/* 颜色 */}
              {dataSource.color && (
                <div className={ItemClass}>
                  <div className={ItemLabelClass}>
                    {LangageAttribute.color[lang]}:{' '}
                    <span className={ItemLabelSpanClass}>
                      {ReplaceAll(dataSource.color as string) || '-'}
                    </span>
                  </div>
                </div>
              )}
              {/* 金属 */}
              {dataSource.hardware && (
                <div className={ItemClass}>
                  <div className={ItemLabelClass}>
                    {LangageAttribute.hardware[lang]}:{' '}
                    <span className={ItemLabelSpanClass}>
                      {ReplaceAll(dataSource.hardware as string) || '-'}
                    </span>
                  </div>
                </div>
              )}
              {/* 刻印 */}
              {dataSource.stamp && (
                <div className={ItemClass} style={{ marginBottom: 0 }}>
                  <div className={ItemLabelClass}>
                    {LangageAttribute.stamp[lang]}:{' '}
                    <span className={ItemLabelSpanClass}>
                      {ReplaceAll(dataSource.stamp as string) || '-'}
                      {showFullStamp && dataSource.fullStamp
                        ? `/${dataSource.fullStamp}`
                        : ''}
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* 价格 */}
          <div className="text-center box-border">
            <div className="inline-flex flex-col box-border">
              {dataSource.referencePrice ? (
                <div className="font-HiraginoMinchoPron text-[8px] tracking-normal w-full text-center mb-1">
                  {`${i18n.t('reference_price', {
                    lng: lang,
                  })}: ${dataSource.currency} ${
                    dataSource.referencePrice?.toLocaleString() || '0'
                  }`}
                </div>
              ) : null}
              <div
                className={classNames(
                  'font-HiraginoMinchoPron font-bold tracking-normal text-center',
                  passportUrl ? 'text-[12px]' : 'text-[14.6667px]',
                )}
              >
                {dataSource.currency}
                &nbsp;
                {dataSource.currency === CURRENCY_MAP.JPY
                  ? priceToWithoutTax(dataSource.price || 0).toLocaleString()
                  : dataSource.price?.toLocaleString() || '0'}
              </div>
              {dataSource.currency === CURRENCY_MAP.JPY && (
                <div className="inline-flex justify-center mt-1 font-HiraginoMinchoPron text-center text-[8px] font-bold tracking-normal">
                  <div>
                    {dataSource.currency}
                    &nbsp;
                    {dataSource.price?.toLocaleString() || '0'}
                  </div>
                  <div>(税込)</div>
                </div>
              )}
            </div>
          </div>

          <div>
            {/* 条形码 */}
            <div className="flex justify-center box-border">
              <svg
                className={classNames(
                  'box-border max-w-full',
                  passportUrl ? 'h-[28px]' : 'h-[37.3281px]',
                )}
                ref={barCodeRef}
              />
            </div>

            <div className="grid gap-1">
              <div className="text-center text-[8px] leading-none flex justify-center gap-1">
                {!!getDatePrice && <span>{getDatePrice}</span>}
                <span>{dataSource.id}</span>
              </div>

              {dataSource.sourceType === 1 && (
                <div className="font-HiraginoMinchoPron text-[8px] text-right pr-4">
                  C
                </div>
              )}
            </div>
          </div>

          <EditOutlined
            className={classNames(
              styles.editBtn,
              'text-[24px] text-[#4096ff] cursor-pointer absolute left-0 top-0',
            )}
            onClick={() => {
              setOpen(true);
              form.setFieldsValue({
                name: dataSource.name,
                material: dataSource.material,
                color: dataSource.color,
                stamp: dataSource.stamp,
                hardware: dataSource.hardware,
                fullStamp: dataSource.fullStamp,
              });
            }}
          />
        </>
      ) : null}

      <Modal
        open={open}
        title={i18n.t('edit')}
        onCancel={() => setOpen(false)}
        onOk={() => {
          const values = form.getFieldsValue();
          const currData = { ...data, ...values };
          setDataSource(currData);
          // 保存到localStorage
          const tempList: PriceTagInfo[] = JSON.parse(
            localStorage.getItem('price-tag') || '[]',
          );
          const index = tempList.findIndex((d) => d.id === dataSource.id);
          tempList[index] = currData;
          localStorage.setItem('price-label', JSON.stringify(tempList));
          setOpen(false);
        }}
      >
        <Form form={form} labelCol={{ span: 6 }}>
          <Form.Item name="name" label={i18n.t('product_name')}>
            <Input />
          </Form.Item>
          <Form.Item name="material" label={i18n.t('material')}>
            <Input.TextArea rows={3} />
          </Form.Item>
          <Form.Item name="color" label={i18n.t('color')}>
            <Input />
          </Form.Item>
          <Form.Item name="hardware" label={i18n.t('hardware')}>
            <Input />
          </Form.Item>
          <Form.Item name="stamp" label={i18n.t('stamp')}>
            <Input />
          </Form.Item>
          <Form.Item name="fullStamp" label={i18n.t('ZPgEroRALL')}>
            <Input />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};
export default memo(PriceTag);
